import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse, type InternalAxiosRequestConfig } from "axios";
import { ElMessage, ElMessageBox } from "element-plus";
import { getToken, removeToken, removeUserInfoCache } from "./auth";
import { APP_TARGET } from "./storage";
import { t } from "./i18n";
import { getRouter } from "@shared/router/router-instance";

/** 扩展请求配置：按需取消重复请求 */
export interface UiRequestConfig extends AxiosRequestConfig {
    /** 开启后，相同 cancelKey（或 method+url+params+data）的新请求会 abort 进行中的旧请求 */
    cancelDuplicate?: boolean;
    /** 自定义去重 key；不传则按 method+url+params+data 生成 */
    cancelKey?: string;
    /** 为 true 时业务码/HTTP 未登录不弹「重新登录」（登出换票等场景） */
    skipUnauthorizedHandler?: boolean;
    /** 为 true 时不弹 ElMessage.error（批量收集失败结果自行展示，如 UiBatchResultDialog） */
    skipErrorMessage?: boolean;
    /** 业务成功码，默认仅 [0]；旧 adv2 接口为 10000 */
    bizSuccessCodes?: number[];
    /** 为 true 时返回完整响应体（含 code/count/summary），不剥离 data */
    returnFullResponse?: boolean;
}

interface InternalRequestConfig extends InternalAxiosRequestConfig {
    cancelDuplicate?: boolean;
    cancelKey?: string;
    skipUnauthorizedHandler?: boolean;
    skipErrorMessage?: boolean;
    bizSuccessCodes?: number[];
    returnFullResponse?: boolean;
    __cancelKey?: string;
}

const pendingRequests = new Map<string, AbortController>();

export function isRequestCanceled(error: unknown): boolean {
    if (axios.isCancel(error)) return true;

    const err = error as { code?: string; name?: string };
    return err?.code === "ERR_CANCELED" || err?.name === "CanceledError";
}

function stableStringify(value: unknown): string {
    if (value === undefined || value === null) return "";
    if (typeof value !== "object") return String(value);

    try {
        return JSON.stringify(value);
    } catch {
        return String(value);
    }
}

function buildCancelKey(config: InternalRequestConfig): string {
    if (config.cancelKey) return config.cancelKey;

    const { method, url, params, data } = config;
    return `${method ?? "get"}&${url ?? ""}&${stableStringify(params)}&${stableStringify(data)}`;
}

function attachCancelDuplicate(config: InternalRequestConfig): InternalRequestConfig {
    if (!config.cancelDuplicate) return config;

    const key = buildCancelKey(config);
    pendingRequests.get(key)?.abort();

    const controller = new AbortController();
    config.signal = controller.signal;
    config.__cancelKey = key;
    pendingRequests.set(key, controller);

    return config;
}

function clearPendingRequest(config: InternalAxiosRequestConfig) {
    const key = (config as InternalRequestConfig).__cancelKey;
    if (!key) return;

    const pending = pendingRequests.get(key);
    if (pending && pending.signal === config.signal) {
        pendingRequests.delete(key);
    }
}

export interface ApiResponse<T = any> {
    code: number;
    message: string;
    data: T;
    [key: string]: any;
}

/** 统一业务异常：message 为后端返回文案 */
export class ApiError extends Error {
    readonly code: number;
    readonly data?: unknown;

    constructor(code: number, message: string, data?: unknown) {
        super(message);
        this.name = "ApiError";
        this.code = code;
        this.data = data;
    }
}

export function isApiError(error: unknown): error is ApiError {
    return error instanceof ApiError;
}

function resolveMessage(message?: string): string {
    return message != null ? String(message).trim() : "";
}

/** 后端约定：登录态失效（token 过期 / 未登录）统一用该业务码（实测 message 为"未登录"），HTTP 401 同样视为失效 */
const UNAUTHORIZED_CODE = 4001;

function isUnauthorizedResult(code: number, httpStatus?: number): boolean {
    return Number(code) === UNAUTHORIZED_CODE || httpStatus === 401;
}

let isHandlingUnauthorized = false;

/** 登录 / 注册 / 跳转换票等访客页：未登录响应不应再弹「重新登录」打断流程 */
function isGuestAuthPath(path: string): boolean {
    return path.startsWith("/login") || path.startsWith("/register") || path.startsWith("/jumpLogin");
}

function shouldSkipUnauthorizedHandler(config?: InternalAxiosRequestConfig): boolean {
    return !!(config as InternalRequestConfig | undefined)?.skipUnauthorizedHandler;
}

/** token 过期 / 未登录：清空本地登录态并弹窗引导重新登录，避免并发请求重复弹窗 */
function handleUnauthorized(message: string): void {
    const router = getRouter();
    const currentPath = router?.currentRoute.value.fullPath ?? "";
    if (isGuestAuthPath(currentPath) || isHandlingUnauthorized) return;

    isHandlingUnauthorized = true;
    removeToken();
    removeUserInfoCache();

    ElMessageBox.alert(message || t("登录状态已过期，请重新登录"), t("提示"), {
        type: "warning",
        confirmButtonText: t("重新登录"),
        confirmButtonClass: "ui-msgbox-btn--auto",
        modal: false,
        showClose: false,
        closeOnClickModal: false,
        closeOnPressEscape: false,
    })
        .catch(() => {})
        .finally(() => {
            isHandlingUnauthorized = false;
            router?.push(`/login?redirect=${encodeURIComponent(currentPath)}`);
        });
}

const service: AxiosInstance = axios.create({
    baseURL: import.meta.env.VITE_API_BASE,
    timeout: 60_000,
    withCredentials: true,
});

service.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        const reqConfig = attachCancelDuplicate(config as InternalRequestConfig);
        const token = getToken();
        if (reqConfig.headers) {
            if (token) reqConfig.headers["Authorization"] = `Bearer ${token}`;
            reqConfig.headers["X-App-Target"] = APP_TARGET;
        }
        return reqConfig;
    },
    error => {
        console.error("[request error]", error);
        return Promise.reject(error);
    },
);

function failBizResponse(res: ApiResponse, config?: InternalAxiosRequestConfig): Promise<never> {
    const message = resolveMessage(res.message);
    const skipErrorMessage = !!(config as InternalRequestConfig | undefined)?.skipErrorMessage;
    if (isUnauthorizedResult(res.code)) {
        if (!shouldSkipUnauthorizedHandler(config)) {
            handleUnauthorized(message);
        }
    } else if (message && !skipErrorMessage) {
        ElMessage.error(message);
    }
    return Promise.reject(new ApiError(res.code, message, res.data));
}

function normalizeHttpError(error: unknown): ApiError {
    if (error instanceof ApiError) return error;

    const axiosError = error as {
        message?: string;
        response?: { status?: number; data?: Partial<ApiResponse> };
    };

    const data = axiosError.response?.data;
    if (data && typeof data.code !== "undefined") {
        const message = resolveMessage(data.message);
        return new ApiError(data.code, message, data.data);
    }

    const status = axiosError.response?.status ?? -1;
    const message = resolveMessage(data?.message) || resolveMessage(axiosError.message);
    return new ApiError(status, message);
}

service.interceptors.response.use(
    (response: AxiosResponse<ApiResponse>) => {
        clearPendingRequest(response.config);
        const res = response.data;

        if (response.config.responseType === "blob" || response.config.responseType === "arraybuffer") {
            return response.data as any;
        }

        if (res === null || res === undefined || typeof res.code === "undefined") {
            return res as any;
        }

        const successCodes = (response.config as InternalRequestConfig).bizSuccessCodes ?? [0];
        if (!successCodes.map(Number).includes(Number(res.code))) {
            return failBizResponse(res, response.config);
        }

        return res as any;
    },
    async error => {
        if (error?.config) {
            clearPendingRequest(error.config);
        }

        if (isRequestCanceled(error)) {
            return Promise.reject(error);
        }

        console.error("[http error]", error);
        const apiError = normalizeHttpError(error);
        const httpStatus = (error as { response?: { status?: number } })?.response?.status;
        if (isUnauthorizedResult(apiError.code, httpStatus)) {
            if (!shouldSkipUnauthorizedHandler(error?.config)) {
                handleUnauthorized(apiError.message);
            }
        } else if (apiError.message && !(error?.config as InternalRequestConfig | undefined)?.skipErrorMessage) {
            ElMessage.error(apiError.message);
        }
        return Promise.reject(apiError);
    },
);

function extractResponseData<T>(res: unknown): T {
    if (res === null || res === undefined) {
        return res as T;
    }

    if (typeof res === "object" && typeof (res as ApiResponse).code !== "undefined") {
        return (res as ApiResponse<T>).data as T;
    }

    return res as T;
}

export async function request<T = any>(config: UiRequestConfig): Promise<T> {
    try {
        const res = await service.request<any, ApiResponse<T> | T>(config);
        if (config.returnFullResponse) {
            return res as T;
        }
        return extractResponseData<T>(res);
    } catch (error) {
        if (isRequestCanceled(error)) {
            throw error;
        }
        if (error instanceof ApiError) {
            throw error;
        }
        throw normalizeHttpError(error);
    }
}

export const http = {
    get: <T = any>(url: string, params?: any, config?: UiRequestConfig) => request<T>({ ...config, url, method: "get", params }),

    post: <T = any>(url: string, data?: any, config?: UiRequestConfig) => request<T>({ ...config, url, method: "post", data }),

    put: <T = any>(url: string, data?: any, config?: UiRequestConfig) => request<T>({ ...config, url, method: "put", data }),

    delete: <T = any>(url: string, params?: any, config?: UiRequestConfig) => request<T>({ ...config, url, method: "delete", params }),
};

export { ElMessageBox };

export default service;
